// WAI-ARIA tabs with automatic activation, shared by the home's tabbed sections:
// arrows/Home/End move focus and select; `.active` + aria state track the selection.

// eslint-disable-next-line no-unused-vars -- placeholder names only for the callable type
type PanelMatcher = (tab: HTMLElement, panel: HTMLElement) => boolean
// eslint-disable-next-line no-unused-vars -- placeholder name only for the callable type
type TabHandler = (tab: HTMLElement) => void

type Options = {
  tablist: HTMLElement | null
  tabs: HTMLElement[]
  panels: HTMLElement[]
  panelMatches: PanelMatcher
  onActivate?: TabHandler
}

export const AriaTablist = ({ tablist, tabs, panels, panelMatches, onActivate }: Options) => {
  const activate = (tab: HTMLElement) => {
    tabs.forEach(t => {
      const isActive = t === tab
      t.classList.toggle("active", isActive)
      t.setAttribute("aria-selected", String(isActive))
      t.tabIndex = isActive ? 0 : -1
    })

    panels.forEach(panel => {
      const isActive = panelMatches(tab, panel)
      panel.classList.toggle("active", isActive)
      panel.setAttribute("aria-hidden", String(!isActive))
    })

    onActivate?.(tab)
  }

  tabs.forEach(tab => tab.addEventListener("click", () => activate(tab)))

  tablist?.addEventListener("keydown", event => {
    const currentIndex = tabs.findIndex(tab => tab === document.activeElement)
    if (currentIndex === -1) return

    let nextIndex: number
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % tabs.length
        break
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    tabs[nextIndex].focus()
    activate(tabs[nextIndex])
  })

  return { activate }
}
